'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_PRODUCT_FEATURE,
  DELETE_PRODUCT_FEATURE,
  GET_PRODUCT_FEATURE,
  GET_PRODUCT_FEATURES,
  UPDATE_PRODUCT_FEATURE,
} from '../productFeaturesQueries'
import { ProductFeatureFormValues } from '../types'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'

const formSchema = z.object({
  code: z.string().optional(),
  product_feature_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  image: z.array(z.string()).optional(),
}) satisfies z.ZodType<ProductFeatureFormValues>

interface ProductFeatureFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function ProductFeatureForm({
  data,
  onDirtyChange,
  onRequestClose,
}: ProductFeatureFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueProductFeature: featureData } = {},
    loading: loadingData,
    error,
  } = useQuery(GET_PRODUCT_FEATURE, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<ProductFeatureFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      product_feature_profiles: [],
      image: [],
    },
  })

  const [updateProductFeature] = useMutation(UPDATE_PRODUCT_FEATURE, {
    onError: e => {
      toast.error('Failed to update product feature')
    },
    onCompleted: () => {
      toast.success('Product feature updated successfully')
    },
    refetchQueries: ['GET_PRODUCT_FEATURES', 'GET_PRODUCT_FEATURE'],
  })

  const [createProductFeature] = useMutation(CREATE_PRODUCT_FEATURE, {
    onError: e => {
      toast.error('Failed to create product feature')
    },
    onCompleted: response => {
      toast.success('Product feature created successfully')
      const newId = response.createProductFeature.id
      setId(newId)
    },
    refetchQueries: ['GET_PRODUCT_FEATURES'],
  })

  const [deleteProductFeature] = useMutation(DELETE_PRODUCT_FEATURE, {
    refetchQueries: ['GET_PRODUCT_FEATURES', 'GetProductFeature'],
  })

  const handleDelete = () => {
    if (id) {
      const count = featureData?.product_containers?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteProductFeature({
              variables: { where: { id } },
            })
            toast.success('Product feature deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete product feature')
          }
        },
        description: count
          ? `This feature is used by ${count} product(s). Deleting it will remove it from those products. This action cannot be undone.`
          : 'Are you sure you want to delete this feature? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  function onSubmit(values: ProductFeatureFormValues) {
    if (id) {
      // Update existing feature using the new custom mutation
      const updateData: any = {
        code: values.code,
        profiles: values.product_feature_profiles.map(p => ({
          id: p.id,
          language: p.language,
          name: p.name || '',
          description: p.description,
        })),
      }

      // Handle image update if provided (extract first URL from array)
      if (values.image && values.image.length > 0) {
        updateData.image = values.image[0]
      }

      updateProductFeature({
        variables: {
          where: { id },
          data: updateData,
        },
      })
    } else {
      // Create new feature using the new custom mutation
      createProductFeature({
        variables: {
          data: {
            code: values.code,
            image:
              values.image && values.image.length > 0
                ? values.image[0]
                : undefined,
            profiles: values.product_feature_profiles.map(p => ({
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (featureData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          featureData.product_feature_profiles?.filter(
            p => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        form.reset({
          code: featureData.code ?? '',
          product_feature_profiles: allProfiles,
          image: featureData.photo?.src ? [featureData.photo.src] : [],
        })
      } else {
        // For NEW forms, initialize with profiles for all languages
        form.reset({
          code: '',
          product_feature_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
          image: [],
        })
      }
    }
  }, [featureData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Product Feature</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Feature Name"
                description="This is the public display name."
                control={form.control}
                name={`product_feature_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Unique identifier code for this feature."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="e.g. wood-finish" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`product_feature_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Photo"
                description="Upload feature photo"
                control={form.control}
                name="image"
                render={field => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <ImageUpload maxImages={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
